/**
 * SVG path `d`-attribute → editor `VectorPath` parser (Tier 2 Group F
 * sub-piece #3 — SVG import).
 *
 * Delegates the actual command parsing to Paper.js, which already
 * handles every SVG path command (M / L / H / V / C / S / Q / T / A / Z),
 * relative vs absolute coordinates, smooth-curve reflection, AND the
 * tricky arc-to-cubic conversion (SVG arcs aren't representable in
 * the editor's cubic-bezier-only `VectorAnchor` shape). Paper's path
 * representation already encodes tangent handles RELATIVE to segment
 * points — matches our `VectorAnchor.inX`/`inY`/`outX`/`outY` semantics
 * exactly, so the segment-to-anchor mapping is mechanical.
 *
 * Why Paper rather than rolling our own:
 *  - Paper is already in deps (`paper@^0.12.18`) and bootstrapped via
 *    `ensurePaperInitialized()` for boolean ops + raster-to-vector
 *    simplification — adding a third consumer is free.
 *  - Re-solving arc → cubic decomposition is ~150 LOC of geometry
 *    we'd have to maintain. Paper's implementation has shipped to
 *    production at scale for years.
 *  - The inverse direction (`VectorPath → SVG d-string`) lives in
 *    `vector-path.ts::pathToSvgData`; using Paper for the import path
 *    means the two halves can diverge in encoding details without
 *    catastrophic round-trip breakage. If we wrote our own importer
 *    that only accepted what `pathToSvgData` emits, opening any SVG
 *    NOT produced by the editor would fail.
 *
 * The fragment-rendering path (`svg-rasterize.ts`) handles features
 * this parser can't represent (`<filter>`, `<mask>`, gradients, etc.)
 * — see that module's docstring for the offscreen-SVG-to-canvas trick.
 *
 * No DOM dependencies in `parseSvgPathData` — Paper's core mode (no
 * canvas attachment) works in happy-dom for tests. The exported
 * function is synchronous; no dynamic imports.
 */

import paper from 'paper/dist/paper-core';
import type { VectorPath } from '../types';
import { ensurePaperInitialized } from './paper-bootstrap';
import { paperPathItemToVectorPath } from './path-ops';

/** Hard cap on the total anchor count across all sub-paths of a
 *  single parsed path. Defends against pathological d-strings with
 *  100,000+ commands that would lock the main thread for many
 *  seconds in Paper's parser AND every downstream consumer (Fabric
 *  rendering, history snapshots, etc.). 50,000 is generous —
 *  Illustrator's typical "Simplify path" output is under 5,000.
 *  Pre-PR review finding (R3#6). */
const MAX_PATH_ANCHOR_COUNT = 50_000;

/** Thrown when a single SVG path's anchor count exceeds the cap.
 *  The walker catches this and emits a `malformed-path` warning,
 *  skipping the layer rather than failing the whole import. */
export class SvgPathComplexityError extends Error {
  constructor(
    public readonly anchorCount: number,
    public readonly limit: number,
  ) {
    super(
      `Path has ${anchorCount} anchors, exceeding the editor's ${limit}-anchor limit. Simplify the path in your source tool first.`,
    );
    this.name = 'SvgPathComplexityError';
  }
}

/** Parse an SVG path `d`-attribute string into the editor's
 *  `VectorPath` shape. Empty / whitespace-only input returns an
 *  empty path (no sub-paths) — caller should treat empty paths as
 *  "skip this layer" rather than emitting a layer with no geometry.
 *
 *  Throws `SvgPathParseError` when Paper rejects the d-string as
 *  malformed (rather than silently producing a partial path) — the
 *  importer surfaces this via a flatten-warning and skips the
 *  element. */
export function parseSvgPathData(d: string): VectorPath {
  const trimmed = d.trim();
  if (trimmed === '') return { subPaths: [] };

  ensurePaperInitialized();

  let item: paper.CompoundPath;
  try {
    // `CompoundPath` handles multi-subpath data (multiple `M` commands)
    // uniformly with single-subpath data — paper internally promotes
    // a single `Path` to a CompoundPath's child when appropriate.
    // `insert: false` keeps the path off paper's active project so
    // it's GC-eligible the moment we stop holding a reference.
    item = new paper.CompoundPath({ pathData: trimmed, insert: false });
  } catch (err) {
    throw new SvgPathParseError(
      `Malformed SVG path data: ${err instanceof Error ? err.message : 'unknown error'}`,
      trimmed,
    );
  }

  // Paper accepts gibberish d-strings silently (treats them as
  // empty) — guard against that here so the caller can distinguish
  // "user authored an empty path" from "we silently dropped their
  // path because Paper couldn't parse it." Both cases land at
  // `subPaths.length === 0` from `paperPathItemToVectorPath`; the
  // pre-Paper trim check above already filtered the legitimate
  // empty-input case.
  const out = paperPathItemToVectorPath(item);
  if (out.subPaths.length === 0) {
    throw new SvgPathParseError(
      'SVG path produced zero sub-paths after parsing (likely malformed but Paper accepted it silently)',
      trimmed,
    );
  }
  // Anchor-count cap — defend against pathological d-strings that
  // would lock the main thread downstream. Counted post-parse so
  // Paper's internal arc-to-cubic expansion is included (an SVG
  // `<path d="M0 0 A...">` with many arcs blows up to many more
  // anchors after Paper's decomposition).
  let totalAnchors = 0;
  for (const sub of out.subPaths) totalAnchors += sub.anchors.length;
  if (totalAnchors > MAX_PATH_ANCHOR_COUNT) {
    throw new SvgPathComplexityError(totalAnchors, MAX_PATH_ANCHOR_COUNT);
  }
  return out;
}

/** Thrown when Paper can't make sense of a d-string. Carries the
 *  offending string so the importer's warning can surface a
 *  diagnostic snippet to the user.
 *
 *  Use case: an SVG with a path like `<path d="not-actual-svg-data" />`
 *  should produce a flatten-warning ("Path data malformed; skipped")
 *  rather than silently dropping the layer. The importer catches this
 *  exception, records a warning, and continues parsing the rest of
 *  the document. */
export class SvgPathParseError extends Error {
  constructor(
    message: string,
    public readonly pathData: string,
  ) {
    super(message);
    this.name = 'SvgPathParseError';
  }
}

// ---------- Primitive → path-data conversion -------------------------------
//
// SVG ships geometric primitives as their own elements (`<rect>`,
// `<circle>`, etc.). The editor's `VectorPath` only models paths, so
// every primitive is converted to a path-data string and run through
// `parseSvgPathData`. This is the same approach Inkscape's "Object →
// Path" command takes, and the same approach Paper.js takes internally
// when its SVG importer hits a primitive — but we do it here so the
// editor's import path stays explicit about what it's representing
// (a path) rather than carrying primitive-typed nodes through the
// pipeline.

/** Convert an SVG `<rect>`'s attributes to a path d-string. Handles
 *  the corner-radius case (rx / ry) by emitting 4 arc commands —
 *  Paper then decomposes those arcs to cubics. */
export function rectToPathData(opts: {
  x: number;
  y: number;
  width: number;
  height: number;
  rx?: number;
  ry?: number;
}): string {
  const { x, y, width, height } = opts;
  // Degenerate rect (zero width or height) — SVG spec says don't
  // render. Return empty so the caller emits a "skipped" warning
  // instead of an invisible layer.
  if (width <= 0 || height <= 0) return '';

  // SVG spec: if rx is given but ry isn't (or vice versa), the
  // missing one inherits the given value. Both clamped to half the
  // matching side (otherwise corners would overlap and the rendering
  // is undefined).
  let rx = opts.rx ?? opts.ry ?? 0;
  let ry = opts.ry ?? opts.rx ?? 0;
  rx = Math.min(Math.max(rx, 0), width / 2);
  ry = Math.min(Math.max(ry, 0), height / 2);

  if (rx === 0 || ry === 0) {
    // Square corners — straight lines all around.
    return `M ${x} ${y} h ${width} v ${height} h ${-width} z`;
  }
  // Rounded corners — 4 arcs at the corners, 4 straight edges
  // between them. SVG arc syntax: `A rx ry x-axis-rotation
  // large-arc-flag sweep-flag x y`.
  const x1 = x + rx;
  const x2 = x + width - rx;
  const y1 = y + ry;
  const y2 = y + height - ry;
  return [
    `M ${x1} ${y}`,
    `L ${x2} ${y}`,
    `A ${rx} ${ry} 0 0 1 ${x + width} ${y1}`,
    `L ${x + width} ${y2}`,
    `A ${rx} ${ry} 0 0 1 ${x2} ${y + height}`,
    `L ${x1} ${y + height}`,
    `A ${rx} ${ry} 0 0 1 ${x} ${y2}`,
    `L ${x} ${y1}`,
    `A ${rx} ${ry} 0 0 1 ${x1} ${y}`,
    'Z',
  ].join(' ');
}

/** Convert an SVG `<circle>`'s attributes to a path d-string. Two
 *  semicircular arcs joined; Paper expands to 4-cubic
 *  approximation. */
export function circleToPathData(opts: { cx: number; cy: number; r: number }): string {
  const { cx, cy, r } = opts;
  if (r <= 0) return '';
  // M (cx-r, cy) — left point
  // A r r 0 1 0 (cx+r, cy) — sweep around to right point (large arc)
  // A r r 0 1 0 (cx-r, cy) — sweep back (large arc, same direction)
  // Z — close (no-op but matches the editor's closed-sub-path
  //     convention).
  return `M ${cx - r} ${cy} A ${r} ${r} 0 1 0 ${cx + r} ${cy} A ${r} ${r} 0 1 0 ${cx - r} ${cy} Z`;
}

/** Convert an SVG `<ellipse>`'s attributes to a path d-string. Same
 *  structure as circle with separate rx / ry. */
export function ellipseToPathData(opts: {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}): string {
  const { cx, cy, rx, ry } = opts;
  if (rx <= 0 || ry <= 0) return '';
  return `M ${cx - rx} ${cy} A ${rx} ${ry} 0 1 0 ${cx + rx} ${cy} A ${rx} ${ry} 0 1 0 ${cx - rx} ${cy} Z`;
}

/** Convert an SVG `<line>`'s endpoints to a path d-string. Two-point
 *  open path. */
export function lineToPathData(opts: { x1: number; y1: number; x2: number; y2: number }): string {
  return `M ${opts.x1} ${opts.y1} L ${opts.x2} ${opts.y2}`;
}

/** Convert an SVG `<polyline>` / `<polygon>`'s `points` attribute to
 *  a path d-string. Polygons close; polylines don't.
 *
 *  SVG's points-attribute grammar is lax — points can be separated by
 *  whitespace or commas or both (`"10,20 30,40"`, `"10 20 30 40"`,
 *  `"10,20,30,40"` are all valid). Splitting on any non-numeric
 *  character handles all three. */
export function pointsToPathData(points: string, closed: boolean): string {
  // Split on whitespace, commas, or any other non-numeric separator.
  // The filter strips empty strings from leading/trailing separators.
  const nums = points
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s !== '')
    .map(Number);
  if (nums.length < 4 || nums.length % 2 !== 0) {
    // Need at least 2 points; odd count is malformed.
    return '';
  }
  const parts: string[] = [`M ${nums[0]} ${nums[1]}`];
  for (let i = 2; i < nums.length; i += 2) {
    parts.push(`L ${nums[i]} ${nums[i + 1]}`);
  }
  if (closed) parts.push('Z');
  return parts.join(' ');
}
